import AppModule from "@/store/modules/app";
import DeviceModule from "@/store/modules/device";
import UserModule from "@/store/modules/user";

import dayjs from "dayjs";
import { generateLongId } from "../utils/helpers/app";
import { request3 } from "@/utils/request";
import { ILog } from "@/interfaces/logs/poslogs";

import { useOrderStore } from "@smeetz/order-store";
import { dexieDB } from "./dexie-database";
import { gzipCompressJson } from "@/utils/helpers/common";
import { LocalStorageKeys } from "./storage.service";

export enum LogLevel {
  Info = "INFO",
  /** Expected lifecycle failures (e.g. expired order session). */
  Warning = "WARNING",
  /** Unexpected failures — the level Graylog alerts should watch. */
  Error = "ERROR",
}

/**
 * JSON.stringify drops an Error's `message` (non-enumerable), so a `{ error }`
 * param used to reach the log backend as `{}`. Expand every Error in the
 * params tree into a plain object: its own enumerable props first (e.g. the
 * orders package's `code`/`status`/`details`, whose nested originalError
 * chain is expanded recursively too), then name/message. The stack is
 * intentionally left out — minified production frames are unreadable in the
 * log backend, and stack traces are Sentry's job.
 */
function expandErrorsReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Error) {
    return {
      ...(value as unknown as Record<string, unknown>),
      name: value.name,
      message: value.message,
    };
  }
  return value;
}

/**
 * Hoists a `requestId` out of the log data so it lands as its own top-level
 * field on the log line (next to orderSessionId), where the log backend can
 * index and filter on it, instead of being buried inside the stringified
 * `params`. Everything else stays in the params payload.
 */
function splitRequestId(data: unknown): {
  requestId?: string;
  params: unknown;
} {
  if (
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    typeof (data as { requestId?: unknown }).requestId === "string"
  ) {
    const { requestId, ...params } = data as Record<string, unknown>;
    return { requestId: requestId as string, params };
  }
  return { params: data };
}

class Logger {
  meta() {
    const selectedOrder = useOrderStore().selectedOrder;
    return {
      // These are default for each app
      env: import.meta.env.VITE_ENV,
      timestamp: Date.now(),
      app: import.meta.env.VITE_NAME,
      logid: generateLongId(),

      // These are specifics for New POS
      date: dayjs().format("YYYY-MM-DD"),
      device: DeviceModule.newLogicDeviceId || "",
      user: UserModule.userId,
      group: UserModule.getUserGroupId,
      lang: AppModule.getLanguageTag,
      // Version of app should be added
      version: AppModule.appVersion,
      channel:
        localStorage.getItem(LocalStorageKeys.ReleaseChannel) || "stable",
      desktopAppVersion: DeviceModule.hardwareInfo?.desktopAppVersion || "",

      order: selectedOrder?.id ?? "",
      orderReference: selectedOrder?.reference ?? "",
      hardwareUuid:
        DeviceModule.hardwareInfo && DeviceModule.hardwareInfo.id
          ? DeviceModule.hardwareInfo.id
          : "",
      hardwareHash: DeviceModule.getHardwareHash || "",
      orderSessionId: selectedOrder?.orderSession?.id ?? "",
    };
  }

  /**
   * Builds one complete log line: hoists the requestId out of the data,
   * serializes the rest into `params` (gzip+base64 for messages prefixed
   * "(compressed)", pretty JSON otherwise) and stamps the per-line meta
   * envelope. Both transports (Dexie batch and direct POST) go through here
   * so their line shapes cannot drift. A serialization failure (circular
   * payload, throwing getter) degrades to a diagnostic params value instead
   * of throwing out of an un-awaited logging call.
   */
  private async buildLine(msg: string, data: any, logLevel: LogLevel) {
    const meta = this.meta();
    const { requestId, params: dataParams } = splitRequestId(data);
    let params: string;
    try {
      params = msg.startsWith("(compressed)")
        ? Buffer.from(await gzipCompressJson(dataParams)).toString("base64")
        : JSON.stringify(
            { ...(dataParams as Record<string, unknown>) },
            expandErrorsReplacer,
            2
          );
    } catch (error) {
      params = JSON.stringify({ serializationError: String(error) });
    }
    return {
      meta,
      line: {
        logLevel,
        message: `${dayjs(meta.timestamp).toISOString()} ${logLevel} : ${msg}`,
        params,
        ...(requestId !== undefined && { requestId }),
        ...meta,
      },
    };
  }

  async info(msg: string, data: any = {}, logLevel: LogLevel = LogLevel.Info) {
    const { meta, line } = await this.buildLine(msg, data, logLevel);
    try {
      this.store({ _id: meta.logid, log: line });
    } catch (error) {
      // Putting it in setTimeout because we don't block
      // app execution because of logging
      setTimeout(() => {
        throw new Error(`Error while info logging ${error}`);
      });
    }
  }

  /**
   * Same as info(), but POSTs the logs to remote server directly
   * @param msg
   * @param data
   * @param logLevel
   */
  async infoDirect(
    msg: string,
    data: any = {},
    logLevel: LogLevel = LogLevel.Info
  ) {
    const { line } = await this.buildLine(msg, data, logLevel);
    try {
      await postLogs([line]);
    } catch (error) {
      throw new Error(`Error while info logging ${error}`);
    }
  }

  /**
   * Sotres a log and persists it to localstorage.
   * Note that logs are not saved while this class is syncing
   * It will attempt to save it for 10 seconds before throwing an error
   */
  async store(log: ILog) {
    await dexieDB.logs.add(log.log);
  }
}

export const LogService = new Logger();

/**
 * POST logs to remote server
 * @param data
 * @returns
 */
export async function postLogs(logs: unknown): Promise<any> {
  const data = { logs: logs };
  const response = await request3({
    method: "POST",
    data,
  });

  return response;
}
